import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { RoomModel } from 'src/models/to-do.model';

export interface ToDoList {
  id: number;
  title: string;
  description?: string;
  before_after_photos_state: string;
  favorited: boolean;
  state: string;
  is_default?: boolean;
  default_list_settings?: { key: string; value: any }[];
}

export interface ToDoListsResponse {
  body: ToDoList[];
  totalRecords: number;
}

export interface CreateListPayload {
  title: string;
  description?: string;
  before_after_photos_state?: string;
  address_id: number;
}

export interface UpdateListPayload {
  title?: string;
  description?: string;
  before_after_photos_state?: string;
}

export interface AddTaskPayload {
  task: {
    title: string;
    note?: string;
    is_important?: boolean;
    address_task_group_id?: number;
    room_object?: {
      name?: string;
      genome_category_id?: number;
    };
    options?: {
      name: string;
      notes?: string;
      required?: boolean;
      allowed_values?: string[];
    }[];
  };
}

export interface UpdateTaskPayload {
  task?: {
    title?: string;
    note?: string;
    is_important?: boolean;
    room_object?: {
      name?: string;
      genome_category_id?: number;
    };
    options?: {
      name: string;
      notes?: string;
      required?: boolean;
      allowed_values?: string[];
    }[];
  };
}

export interface AddGroupPayload {
  name: string;
  address_id: number;
}

export interface UpdateGroupPayload {
  address_task_list_id: number;
}

export interface AddPhotoNotePayload {
  photo_notes: {
    type: 'task' | 'group' | 'room' | 'room_object';
    object_id: number;
    photo_url: string;
    caption?: string;
    s3_photo_url_key?: string;
  }[];
}

export interface AddTextNotePayload {
  text_notes: {
    type: 'address_task_group';
    object_id: number;
    body: string;
  }[];
}

export interface SetItemsOrderPayload {
  id: number;
  items_in_order: { type: string; id: number }[];
}

export interface TaskCategoryOption {
  id: number;
  name: string;
  notes?: string;
  allowed_values?: string[];
}

export interface AppliedJob {
  id: number;
  date: string;
  state: string;
}

export interface ListSettingsPayload {
  settings: { key: string; value: any }[];
}

export interface CopyListPayload {
  target_address_id: number;
}

@Injectable({ providedIn: 'root' })
export class ToDosApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.api_url;

  // List Operations
  getAllLists(perPage: number, pageIndex: number, addressIds?: number | null): Observable<ToDoListsResponse> {
    let url = `${this.baseUrl}api/v1/customer/task-lists?per_page=${perPage}&page=${pageIndex}`;
    if (addressIds) {
      url += `&filters[address_ids]=${addressIds}`;
    }
    return this.http.get<ToDoList[]>(url, { observe: 'response' }).pipe(
      map((response: HttpResponse<ToDoList[]>) => ({
        body: response.body || [],
        totalRecords: Number(response.headers.get('X-Total-Records')) || 0,
      }))
    );
  }

  getListsForAddress(addressId: number): Observable<ToDoList[]> {
    const url = `${this.baseUrl}api/v1/customer/task-lists?per_page=1000`;
    return this.http.get<ToDoList[]>(url).pipe(
      map((lists) => lists.map((list) => {
          const hasDefault = list.default_list_settings?.some((setting) =>
            setting.key.includes('default_list.address')
          );
          return { ...list, is_default: hasDefault };
        }))
    );
  }

  getOrphanedLists(): Observable<ToDoList[]> {
    const url = `${this.baseUrl}api/v1/customer/task-lists?per_page=1000&filters[only_orphaned]=true`;
    return this.http.get<ToDoList[]>(url).pipe(
      map((lists) => lists.map((list) => {
          const hasDefault = list.default_list_settings?.some((setting) =>
            setting.key.includes('default_list.address')
          );
          return { ...list, is_default: hasDefault };
        }))
    );
  }

  getListDetail(listId: number): Observable<RoomModel[]> {
    const url = `${this.baseUrl}api/v1/customer/task-lists/${listId}/tasks?includes=tasks,notes,photos`;
    return this.http.get<RoomModel[]>(url);
  }

  createList(payload: CreateListPayload): Observable<ToDoList> {
    const url = `${this.baseUrl}api/v1/customer/task-lists`;
    return this.http.post<ToDoList>(url, payload);
  }

  updateList(listId: number, payload: UpdateListPayload): Observable<ToDoList> {
    const url = `${this.baseUrl}api/v1/customer/task-lists/${listId}`;
    return this.http.put<ToDoList>(url, payload);
  }

  deleteList(listId: number): Observable<void> {
    const url = `${this.baseUrl}api/v1/customer/task-lists/${listId}`;
    return this.http.delete<void>(url);
  }

  copyList(fromAddressId: number, listId: number, payload: CopyListPayload): Observable<ToDoList> {
    const url = `${this.baseUrl}api/v1/customer/addresses/${fromAddressId}/task-lists/${listId}/copy-task-list`;
    return this.http.post<ToDoList>(url, payload);
  }

  // Default List Operations
  updateDefaultList(addressId: number, listId: number): Observable<void> {
    const url = `${this.baseUrl}api/v1/customer/addresses/${addressId}/assign-default-address-task-list`;
    return this.http.put<void>(url, { address_task_list_id: listId });
  }

  saveListSettings(listId: number, payload: ListSettingsPayload): Observable<void> {
    const url = `${this.baseUrl}api/v1/customer/task-lists/${listId}/settings`;
    return this.http.put<void>(url, payload);
  }

  // Task Operations
  addTask(addressId: number, listId: number, payload: AddTaskPayload): Observable<any> {
    const url = `${this.baseUrl}api/v1/customer/addresses/${addressId}/task-lists/${listId}/tasks`;
    return this.http.post(url, payload);
  }

  updateTask(addressId: number, listId: number, taskId: number, payload: UpdateTaskPayload): Observable<any> {
    const url = `${this.baseUrl}api/v1/customer/addresses/${addressId}/task-lists/${listId}/tasks/${taskId}`;
    return this.http.put(url, payload);
  }

  deleteTask(addressId: number, listId: number, taskId: number): Observable<void> {
    const url = `${this.baseUrl}api/v1/customer/addresses/${addressId}/task-lists/${listId}/tasks/${taskId}`;
    return this.http.delete<void>(url);
  }

  updateTaskOrder(listId: number, payload: { address_task_group_id: number; task_ids_in_order: number[] }): Observable<void> {
    const url = `${this.baseUrl}api/v1/customer/task-lists/${listId}/set-tasks-display-order`;
    return this.http.put<void>(url, payload);
  }

  // Group/Room Operations
  addGroup(payload: AddGroupPayload): Observable<any> {
    const url = `${this.baseUrl}api/v1/customer/address-task-groups`;
    return this.http.post(url, payload);
  }

  addGroupToList(groupId: number, payload: UpdateGroupPayload): Observable<any> {
    const url = `${this.baseUrl}api/v1/customer/address-task-groups/${groupId}/add-to-task-list`;
    return this.http.post(url, payload);
  }

  updateGroupName(groupId: number, payload: { name: string }): Observable<any> {
    const url = `${this.baseUrl}api/v1/customer/address-task-groups/${groupId}`;
    return this.http.put(url, payload);
  }

  removeGroupFromList(groupId: number, listId: number): Observable<void> {
    const url = `${this.baseUrl}api/v1/customer/address-task-groups/${groupId}/remove-from-task-list`;
    return this.http.post<void>(url, { address_task_list_id: listId });
  }

  updateMultipleGroups(listId: number, payload: { address_task_group_ids: number[] }): Observable<void> {
    const url = `${this.baseUrl}api/v1/customer/task-lists/${listId}/set-task-groups`;
    return this.http.put<void>(url, payload);
  }

  updateGroupPriority(listId: number, orderedGroupIds: number[]): Observable<void> {
    const url = `${this.baseUrl}api/v1/customer/task-lists/${listId}/set-groups-display-order`;
    return this.http.put<void>(url, {
      address_task_list_id: listId,
      address_task_group_ids_in_order: orderedGroupIds,
    });
  }

  setItemsDisplayOrder(groupId: number, itemsInOrder: { type: string; id: number }[]): Observable<void> {
    const url = `${this.baseUrl}api/v1/customer/address-task-groups/${groupId}/set-items-display-order`;
    return this.http.put<void>(url, { id: groupId, items_in_order: itemsInOrder });
  }

  // Notes Operations
  addTextNote(payload: AddTextNotePayload): Observable<any> {
    const url = `${this.baseUrl}api/v1/customer/text-notes`;
    return this.http.post(url, payload);
  }

  updateTextNote(noteId: number, body: string): Observable<any> {
    const url = `${this.baseUrl}api/v1/customer/text-notes/${noteId}`;
    return this.http.put(url, { body });
  }

  deleteTextNote(noteId: number): Observable<void> {
    const url = `${this.baseUrl}api/v1/customer/text-notes/${noteId}`;
    return this.http.delete<void>(url);
  }

  // Photo Notes Operations
  addPhotoNote(payload: AddPhotoNotePayload): Observable<any> {
    const url = `${this.baseUrl}api/v1/customer/photo-notes`;
    return this.http.post(url, payload);
  }

  // Task Category Options
  getTaskCategoryOptions(): Observable<TaskCategoryOption[]> {
    const url = `${this.baseUrl}api/v1/customer/task-category-options`;
    return this.http.get<TaskCategoryOption[]>(url);
  }

  // Applied Jobs
  getAppliedJobs(listId: number): Observable<AppliedJob[]> {
    const url = `${this.baseUrl}api/v1/customer/task-lists/${listId}/applied-jobs`;
    return this.http.get<AppliedJob[]>(url);
  }

  assignListToJobs(jobIds: number[], listId: number): Observable<void> {
    const url = `${this.baseUrl}api/v1/customer/cleanings/assign-address-task-list-jobs`;
    return this.http.put<void>(url, { job_ids: jobIds, address_task_list_id: listId });
  }

  // Job To-Dos
  getToDosForJob(jobId: number): Observable<any> {
    const url = `${this.baseUrl}api/v1/customer/cleanings/${jobId}/rooms`;
    return this.http.get(url);
  }

  updateJobToDos(jobId: number, payload: any): Observable<void> {
    const url = `${this.baseUrl}api/v1/customer/cleanings/${jobId}/to-dos`;
    return this.http.put<void>(url, payload);
  }
}
